import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { forwardRef, useRef, useEffect } from 'react';
import useCallbackRef from '../../../hooks/use-callback-ref.esm.js';
import { findMatchingElement } from '../../../utils/dom.esm.js';
import ownerDocument from '../../../utils/owner-document.esm.js';
import { mergeRefs } from '../../../utils/react-helpers.esm.js';

var _excluded = ["ignoreElement", "children", "disableReactTree", "mouseEvent", "onClickAway", "touchEvent"];
function mapEventPropToEvent(eventProp) {
  return eventProp.substring(2).toLowerCase();
}
function clickedRootScrollbar(event) {
  return document.documentElement.clientWidth < event.clientX || document.documentElement.clientHeight < event.clientY;
}

/**
 * Listen for click events that occur somewhere in the document, outside of the element itself.
 * For instance, if you need to hide a menu when people click anywhere else on your page.
 */
var ClickAwayListener = /*#__PURE__*/forwardRef(function ClickAwayListener(props, ref) {
  var _ignoreElement = props.ignoreElement,
    children = props.children,
    _props$disableReactTr = props.disableReactTree,
    disableReactTree = _props$disableReactTr === void 0 ? false : _props$disableReactTr,
    _props$mouseEvent = props.mouseEvent,
    mouseEvent = _props$mouseEvent === void 0 ? 'onClick' : _props$mouseEvent,
    onClickAway = props.onClickAway,
    _props$touchEvent = props.touchEvent,
    touchEvent = _props$touchEvent === void 0 ? 'onTouchEnd' : _props$touchEvent,
    rest = _objectWithoutProperties(props, _excluded);
  var ignoreElement = useRef(_ignoreElement);
  // update only if it's not null or undefined
  ignoreElement.current = _ignoreElement ? _ignoreElement : ignoreElement.current;
  var movedRef = useRef(false);
  var nodeRef = useRef();
  var mountedRef = useRef(false);
  var syntheticEventRef = useRef(false);
  useEffect(function () {
    mountedRef.current = true;
    return function () {
      mountedRef.current = false;
    };
  }, []);
  // @ts-ignore
  var handleRef = mergeRefs(ref, children.ref, nodeRef);

  // The handler doesn't take event.defaultPrevented into account:
  //
  // event.preventDefault() is meant to stop default behaviors like
  // clicking a checkbox to check it, hitting a button to submit a form,
  // and hitting left arrow to move the cursor in a text input etc.
  // Only special HTML elements have these default behaviors.
  var handleClickAway = useCallbackRef(function (event) {
    // Given developers can stop the propagation of the synthetic event,
    // we can only be confident with a positive value.
    var insideReactTree = syntheticEventRef.current;
    syntheticEventRef.current = false;

    // 1. IE 11 support, which trigger the handleClickAway even after the unbind
    // 2. The child might render null.
    // 3. Behave like a blur listener.
    if (!mountedRef.current || !nodeRef.current || clickedRootScrollbar(event)) {
      return;
    }

    // Do not act if user performed touchmove
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    var insideDOM;

    // If not enough, can use https://github.com/DieterHolvoet/event-propagation-path/blob/master/propagationPath.js
    // @ts-ignore
    if (event.composedPath) {
      // @ts-ignore
      insideDOM = event.composedPath().indexOf(nodeRef.current) > -1;
    } else {
      var doc = ownerDocument(nodeRef.current);
      // TODO v6 remove dead logic https://caniuse.com/#search=composedPath.
      // `doc.contains` works in modern browsers but isn't supported in IE 11:
      // https://github.com/timmywil/panzoom/issues/450
      // https://github.com/videojs/video.js/pull/5872
      insideDOM = !(doc.documentElement && doc.documentElement.contains(event.target)) || nodeRef.current.contains(event.target);
    }
    if (
    // we will check only 4 level up from the optential target
    // this should cover most of our use case for popover anchor element
    !(ignoreElement.current && findMatchingElement(event.target, ignoreElement.current, 4)) && !insideDOM && (disableReactTree || !insideReactTree)) {
      onClickAway(event);
    }
  });

  // Keep track of mouse/touch events that bubbled up through the portal.
  var createHandleSynthetic = function createHandleSynthetic(handlerName) {
    return function (event) {
      syntheticEventRef.current = true;
      var childrenPropsHandler =
      //@ts-ignore
      children && children.props && children.props[handlerName];
      if (childrenPropsHandler) {
        childrenPropsHandler(event);
      }
    };
  };
  var childrenProps = {
    ref: handleRef
  };
  if (touchEvent !== false) {
    childrenProps[touchEvent] = createHandleSynthetic(touchEvent);
  }
  useEffect(function () {
    if (touchEvent !== false) {
      var mappedTouchEvent = mapEventPropToEvent(touchEvent);
      var doc = ownerDocument(nodeRef.current);
      var handleTouchMove = function handleTouchMove() {
        movedRef.current = true;
      };

      // @ts-ignore
      doc.addEventListener(mappedTouchEvent, handleClickAway);
      doc.addEventListener('touchmove', handleTouchMove);
      return function () {
        // @ts-ignore
        doc.removeEventListener(mappedTouchEvent, handleClickAway);
        doc.removeEventListener('touchmove', handleTouchMove);
      };
    }
    return undefined;
  }, [handleClickAway, touchEvent]);
  if (mouseEvent !== false) {
    childrenProps[mouseEvent] = createHandleSynthetic(mouseEvent);
  }
  useEffect(function () {
    if (mouseEvent !== false) {
      var mappedMouseEvent = mapEventPropToEvent(mouseEvent);
      var doc = ownerDocument(nodeRef.current);
      // @ts-ignore
      doc.addEventListener(mappedMouseEvent, handleClickAway);
      return function () {
        // @ts-ignore
        doc.removeEventListener(mappedMouseEvent, handleClickAway);
      };
    }
    return undefined;
  }, [handleClickAway, mouseEvent]);
  return children ? /*#__PURE__*/React.cloneElement(children, _objectSpread2({}, _objectSpread2(_objectSpread2({}, childrenProps), rest))) : null;
});

export default ClickAwayListener;
export { ClickAwayListener };
//# sourceMappingURL=click-away-listener.esm.js.map
